/**
 * Step 2 of the catering wizard — menu selection + estimate + submit.
 * Extracted from CateringSection.jsx. Stateless: receives menu list, current
 * selections (qty by item id), and action callbacks from the parent.
 */
import React from "react";
import { ArrowLeft, Plus, Minus, Wand2, Loader2, MessageCircle } from "lucide-react";
import { format } from "date-fns";
import { es as esLocale, enUS } from "date-fns/locale";
import { useApp } from "../../AppContext";

const CateringMenuStep = ({
    menu,
    menuLoading,
    selections,
    setQty,
    autoFill,
    onBack,
    onSubmit,
    submitting,
    done,
    cateringWaUrl,
    form,
    estimated,
    totalItems,
    recommended,
}) => {
    const { t, lang } = useApp();
    return (
        <div data-testid="catering-menu-step" className="space-y-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <button
                    type="button"
                    data-testid="catering-back-btn"
                    onClick={onBack}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-earth-700 hover:text-terracotta-600"
                >
                    <ArrowLeft className="w-4 h-4" />
                    {lang === "es" ? "Editar detalles" : "Edit details"}
                </button>
                <button
                    type="button"
                    data-testid="catering-autofill-btn"
                    onClick={autoFill}
                    disabled={menuLoading}
                    className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-leaf-600 hover:text-leaf-700"
                    title={
                        lang === "es"
                            ? `Sugerir ${recommended} porciones de cada destacado`
                            : `Suggest ${recommended} portions of each featured dish`
                    }
                >
                    <Wand2 className="w-3.5 h-3.5" />
                    {lang === "es" ? "Sugerir menú" : "Auto-fill"}
                </button>
            </div>

            <div className="rounded-2xl bg-earth-100/60 border border-earth-200 p-4 text-sm">
                <div className="font-bold text-earth-900">
                    {form.customer_name} ·{" "}
                    <span className="text-terracotta-600">
                        {form.guest_count} {lang === "es" ? "invitados" : "guests"}
                    </span>
                </div>
                <div className="text-xs text-earth-600 mt-0.5">
                    {form.event_date
                        ? format(form.event_date, "PPP", { locale: lang === "es" ? esLocale : enUS })
                        : "—"}{" "}
                    · {t.catering.types[form.event_type]}
                </div>
            </div>

            <div className="text-xs uppercase tracking-wider font-bold text-earth-600">
                {lang === "es" ? "Elige tus platos" : "Pick your dishes"}
            </div>

            {menuLoading && (
                <div className="text-earth-600 text-sm">{t.menu.empty}</div>
            )}

            <ul className="space-y-3 max-h-[440px] overflow-y-auto pr-1">
                {menu.map((m) => {
                    const qty = selections[m.id] || 0;
                    const name = lang === "es" ? m.name_es : m.name_en;
                    const serving = lang === "es" ? m.serving_size_es : m.serving_size_en;
                    return (
                        <li
                            key={m.id}
                            data-testid={`catering-item-${m.slug}`}
                            className="flex gap-3 items-center bg-earth-50 border border-earth-200 rounded-2xl p-3"
                        >
                            <img
                                src={m.image_url}
                                alt=""
                                className="w-16 h-16 rounded-xl object-cover shrink-0"
                                loading="lazy"
                            />
                            <div className="flex-1 min-w-0">
                                <div className="font-serif font-bold text-earth-900 text-sm truncate">{name}</div>
                                {serving && (
                                    <div className="text-[10px] uppercase tracking-wider font-bold text-leaf-600 mt-0.5 truncate">
                                        🍽 {serving}
                                    </div>
                                )}
                                <div className="text-terracotta-600 font-bold text-xs">${m.price.toFixed(2)}</div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <button
                                    type="button"
                                    data-testid={`catering-dec-${m.slug}-btn`}
                                    onClick={() => setQty(m.id, Math.max(0, qty - 1))}
                                    className="w-8 h-8 rounded-full bg-white border border-earth-200 hover:bg-earth-100 flex items-center justify-center"
                                >
                                    <Minus className="w-3.5 h-3.5" />
                                </button>
                                <span
                                    className="text-sm font-bold w-8 text-center"
                                    data-testid={`catering-qty-${m.slug}`}
                                >
                                    {qty}
                                </span>
                                <button
                                    type="button"
                                    data-testid={`catering-inc-${m.slug}-btn`}
                                    onClick={() => setQty(m.id, qty + 1)}
                                    className="w-8 h-8 rounded-full bg-earth-900 text-white hover:bg-terracotta-500 flex items-center justify-center"
                                >
                                    <Plus className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </li>
                    );
                })}
            </ul>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-5 border-t border-earth-200">
                <div>
                    <div className="text-xs uppercase tracking-wider text-earth-600 font-bold">
                        {lang === "es" ? "Total estimado" : "Estimated total"}
                    </div>
                    <div className="flex items-baseline gap-3">
                        <span
                            data-testid="catering-estimated-total"
                            className="font-serif font-black text-3xl text-earth-900"
                        >
                            ${estimated.toFixed(2)}
                        </span>
                        <span
                            className="text-xs text-earth-500"
                            data-testid="catering-total-items"
                        >
                            {totalItems} {lang === "es" ? "porciones" : "portions"}
                        </span>
                    </div>
                </div>
                <button
                    type="button"
                    data-testid="catering-confirm-btn"
                    onClick={onSubmit}
                    disabled={submitting || done || totalItems === 0}
                    className="btn-primary"
                    style={{ background: done ? "#4A7C59" : undefined }}
                >
                    {submitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <MessageCircle className="w-4 h-4" />
                    )}
                    {done
                        ? t.catering.success
                        : lang === "es"
                        ? "Confirmar y enviar"
                        : "Confirm & send"}
                </button>
            </div>
            {done && cateringWaUrl && (
                <a
                    href={cateringWaUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid="open-catering-whatsapp-btn"
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl bg-[#25D366] hover:bg-[#1ebe5d] text-white text-base font-bold shadow-lg transition-colors animate-pulse"
                >
                    <MessageCircle className="w-5 h-5" />
                    {lang === "es"
                        ? "👉 Tocá aquí para enviar tu solicitud por WhatsApp"
                        : "👉 Tap here to send your request via WhatsApp"}
                </a>
            )}
        </div>
    );
};

export default CateringMenuStep;
